<script lang="ts" setup>

const emit = defineEmits<{
  (e: 'click'): void
}>();

const props = withDefaults(defineProps<{
  item: AppSearchDebtor;
  showAssociations?: boolean;
  activeButton?: boolean;
}>(), {
  showAssociations: false,
  activeButton: true
});

function formatAddress(address: AppAssociationDTO | null): string {
  if (!address) return '';

  return `${address.Address}, ${address.ZipCode} ${address.City}`;
}

const { t } = useEkI18n();
const address = computed(() => `${props.item.BillingAddress?.Address}, ${props.item.BillingAddress?.ZipCode} ${props.item.BillingAddress?.City}`);
const addressFormat = computed(() => address.value ?? t('labels.notAvailable'));
const emailFormat = computed(() => props.item.Email && props.item.Email !== '' ? props.item.Email : t('labels.notAvailable'));
const phoneFormat = computed(() => props.item.Phone && props.item.Phone !== '' ? props.item.Phone : t('labels.notAvailable'));
const eanFormat = computed(() => {
  const ean = props.item.BillingAddress?.EAN;

  return ean?.length ? ean : t('labels.notAvailable');
});

</script>

<template>
  <IonCard
    :button="activeButton"
    @keydown.enter="emit('click')"
    @click="emit('click')"
  >
    <IonCardHeader>
      <div>
        <WorksheetsBadge
          v-if="item.IsLocked"
          :text="t('customer.locked')"
          color="var(--ion-color-danger-contrast)"
          background="var(--ion-color-danger)"
        >
          <template #icon>
            <IonIcon :icon="ioniconsBanOutline" />
          </template>
        </WorksheetsBadge>
      </div>
      <IonCardTitle :color="item.IsLocked ? 'danger' : ''">
        {{ item.Name }}
      </IonCardTitle>
      <div
        v-if="item.RequisitionNumber"
        class="subtitle-flex"
      >
        <IonCardSubtitle>{{ item.RequisitionNumber }}</IonCardSubtitle>
      </div>
      <div
        v-if="item.SearchName"
        class="subtitle-flex"
      >
        <IonCardSubtitle class="discreet">
          {{ item.SearchName }}
        </IonCardSubtitle>
      </div>
    </IonCardHeader>

    <IonCardContent>
      <div class="detail-container">
        <IonRow class="detail">
          <IonIcon class="fa-fw fa-light fa-location-dot" />
          <span> {{ addressFormat }} </span>
        </IonRow>
        <IonRow
          v-if="item.BillingAddress?.Address2 && item.BillingAddress?.Address2.length" 
          class="detail"
        >
          <IonIcon class="fa-fw fa-light fa-turn-down-right" />
          <span> {{ item.BillingAddress?.Address2 }} </span>
        </IonRow>
        <IonRow class="detail">
          <IonIcon class="fa-fw fa-light fa-phone" />
          <span>{{ phoneFormat }}</span>
        </IonRow>
        <IonRow class="detail">
          <IonIcon class="fa-fw fa-light fa-envelope" />
          <span>{{ emailFormat }}</span>
        </IonRow>
        <IonRow class="detail">
          <IonIcon class="fa-fw fa-light fa-rectangle-barcode" />
          <span>{{ eanFormat }}</span>
        </IonRow>
      </div>

      <div
        v-if="showAssociations && item.Associations && item.Associations.length > 0"
        class="associations-container"
      >
        <div
          v-for="association in item.Associations"
          :key="association.ID"
          class="association-detail"
        >
          <IonRow
            v-if="association.Name"
            class="detail"
          >
            <IonIcon class="fa-fw fa-light fa-user" />
            <span>{{ association.Name }}</span>
          </IonRow>
          <IonRow
            v-if="association.Phone"
            class="detail"
          >
            <IonIcon class="fa-fw fa-light fa-phone" />
            <span>{{ association.Phone }}</span>
          </IonRow>
          <IonRow
            v-if="association.Email"
            class="detail"
          >
            <IonIcon class="fa-fw fa-light fa-envelope" />
            <span>{{ association.Email }}</span>
          </IonRow>
          <IonRow
            v-if="association.Address || association.City || association.ZipCode || association.Country"
            class="detail"
          >
            <IonIcon class="fa-fw fa-light fa-location-dot" />
            <span>{{ formatAddress(association) }}</span>
          </IonRow>
          <IonRow
            v-if="association.EAN"
            class="detail"
          >
            <IonIcon class="fa-fw fa-light fa-rectangle-barcode" />
            <span>{{ association.EAN }}</span>
          </IonRow>
        </div>
      </div>
    </IonCardContent>
  </IonCard>
</template>

<style scoped>
.subtitle-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-container {
  gap: 0.3rem;
  display: flex;
  flex-direction: column;
}

.detail {
  align-items: center;
  gap: 0.5rem;
}

:deep(.fa-fw) {
  padding-top: 0.1rem;
}

.subtitle-locked {
  display: flex;
  align-items: center;
  gap: 4px;
}

.associations-container {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--ion-color-light);
}

.association-detail {
  margin-bottom: 0.5rem;
  padding: 0.3rem;
  background-color: var(--ion-background-color);
  border-radius: 4px;
}

.association-detail .detail {
  font-size: small;
  color: var(--ion-color-medium);
  gap: 0.3rem;
}
ion-card:focus {
  background: #E6E6E6;
  outline: none;
  transition: background 0.2s;
}

</style>
